import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ICreateEngineFiscalRulesState } from "@/@types/engine-fiscal-rules";

const tipMap: Record<number, string> = {
  1: "ICMS",
  2: "PIS",
  3: "COFINS",
  6: "IBS",
  7: "CBS",
};

interface TaxItemsCardProps {
  items: ICreateEngineFiscalRulesState[];
  onRemove: (item: ICreateEngineFiscalRulesState) => void;
  maxHeight?: number;
  className?: string;
}

export function TaxItemsCard({
  items,
  onRemove,
  maxHeight = 400,
  className,
}: TaxItemsCardProps) {
  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <Plus className="h-8 w-8 text-gray-400" />
        </div>
        <p className="font-semibold text-gray-700 text-lg mb-1">
          Nenhuma alíquota cadastrada
        </p>
        <p className="text-sm text-gray-500">
          Clique em <strong>"Adicionar"</strong> para começar
        </p>
      </div>
    );
  }

  return (
    <div
      className={`
        flex flex-col gap-3 
        overflow-y-auto pr-2
        scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
        hover:scrollbar-thumb-gray-400 transition-all duration-200
        ${className || ""}
      `}
      style={{ maxHeight: `${maxHeight}px` }}
    >
      {items.map((item, index) => {
        const formattedDate = item.initial_term
          ? format(new Date(item.initial_term + "T00:00:00"), "dd/MM/yyyy", {
              locale: ptBR,
            })
          : "—";

        const formattedFinalDate = item.final_term
          ? format(new Date(item.final_term + "T00:00:00"), "dd/MM/yyyy", {
              locale: ptBR,
            })
          : null;

        const tip =
          typeof item.tip === "string" ? parseInt(item.tip) : item.tip;
        const tipLabel = tipMap[tip] || item.tip;
        const showClassTrib = tip === 6 || tip === 7;

        return (
          <Card
            key={index}
            className="w-full border border-gray-200/70 bg-gradient-to-br from-white to-gray-50 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl"
          >
            <CardContent className="w-full flex items-center justify-between py-4 px-4">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">
                    {item.state}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span>{tipLabel}</span>
                </div>

                <div className="text-gray-800 font-bold text-lg">
                  {Number(item.alq).toFixed(2).replace(".", ",")}%
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-gray-600">
                  {item.cst && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 font-medium">CST:</span>
                      <span className="font-semibold text-gray-800">
                        {item.cst}
                      </span>
                    </div>
                  )}

                  {item.cfop && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 font-medium">CFOP:</span>
                      <span className="font-semibold text-gray-800">
                        {item.cfop}
                      </span>
                    </div>
                  )}

                  {showClassTrib && item.classification_tax && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 font-medium">
                        Class. Trib.:
                      </span>
                      <span className="font-semibold text-gray-800">
                        {item.classification_tax}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 font-medium">Vigência:</span>
                    <span className="font-semibold text-gray-800">
                      {formattedDate}{" "}
                      {formattedFinalDate && `até ${formattedFinalDate}`}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onRemove(item)}
                className="flex items-center gap-2 text-red-600 hover:text-white hover:bg-red-600/80 transition-all duration-200 rounded-md border border-red-200 hover:border-transparent"
                title="Remover alíquota"
              >
                <Trash2 className="h-4 w-4" />
                <span>Remover</span>
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
